//#region types
import { AuthEnums } from "models/enums";
import { WintSuperkollAPIModelQualifiedShareDividend, WintUtilsWebApi } from "wint-api-client/dist/types";
import { TranslationKeys } from "translations";
import { TagsProps } from "models/propTypes/commonPropTypes";
//#endregion
import useAPIClientHelper from "./useAPIClientHelper";
import { FileHelper, LocalizationHelperInstance } from "utils/helpers";
import cssExports from "theme/styles/main.module.scss";

function useQualifiedShareDividendsService() {
  const { apiClientInstance } = useAPIClientHelper();

  const approveDividend = (id: string) => {
    return apiClientInstance(AuthEnums.AuthType.Company, { showErrors: true }).QualifiedShareDividendClient.Approve(id);
  };

  const rejectDividend = (id: string, message?: string) => {
    return apiClientInstance(AuthEnums.AuthType.Company, {
      showErrors: true,
    })
      .QualifiedShareDividendClient.Reject(id, {
        comment: message,
      } as WintSuperkollAPIModelQualifiedShareDividend.QualifiedShareDividendRejectRequest)
      .then((res) => res.data);
  };

  const downloadPdf = (id: string) => {
    apiClientInstance(AuthEnums.AuthType.Company, {
      responseType: "arraybuffer",
    })
      .QualifiedShareDividendClient.DownloadPdf(id)
      .then((res) => {
        FileHelper.download(res);
      });
  };

  const getTodoState = (): TagsProps => {
    return {
      tags: [LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Common.ToApprove)],
      className: cssExports.yellowTag,
    };
  };

  const hasQualifiedShareDividendReportPermissions = () => {
    return true;
  };

  const getQualifiedShareDividend = async (id: string) => {
    return await apiClientInstance(AuthEnums.AuthType.Company, {
      showErrors: true,
    })
      .QualifiedShareDividendClient.Get(id)
      .then((res) => res.data);
  };

  const getCalculatedValues = async (id: string) => {
    return await apiClientInstance(AuthEnums.AuthType.Company, {
      showErrors: true,
    })
      .QualifiedShareDividendClient.GetCalculatedValues(id)
      .then((res) => res.data)
      .catch(() => {});
  };

  const reportFilter = (baseFilter: WintUtilsWebApi.Filter) => {
    return apiClientInstance(AuthEnums.AuthType.Company, {
      showErrors: true,
    }).QualifiedShareDividendClient.Filter({
      ...baseFilter,
    });
  };

  const getRuleText = (rule: WintSuperkollAPIModelQualifiedShareDividend.QualifiedShareDivedendRule) => {
    return LocalizationHelperInstance.getFormattedMessage(`QualifiedShareDividends.Rule_${rule}`);
  };

  const getStateText = (state: WintSuperkollAPIModelQualifiedShareDividend.QualifiedShareDividendState) => {
    return LocalizationHelperInstance.getFormattedMessage(`QualifiedShareDividends.State_${state}`);
  };

  const getStateLabelClass = (state: WintSuperkollAPIModelQualifiedShareDividend.QualifiedShareDividendState) => {
    switch (state) {
      case WintSuperkollAPIModelQualifiedShareDividend.QualifiedShareDividendState.AwaitingApproval:
        return cssExports.yellowTag;
      case WintSuperkollAPIModelQualifiedShareDividend.QualifiedShareDividendState.Done:
        return cssExports.greenTag;
      default:
        return cssExports.blueTag;
    }
  };

  const isStateToApprove = (state: WintSuperkollAPIModelQualifiedShareDividend.QualifiedShareDividendState) =>
    state === WintSuperkollAPIModelQualifiedShareDividend.QualifiedShareDividendState.AwaitingApproval;

  const getShareholdersDividendInfo = (financialYearId: number) => {
    return apiClientInstance(AuthEnums.AuthType.Company, {
      showErrors: true,
    })
      .QualifiedShareDividendClient.GetShareholderDividendsInfoAsync(financialYearId)
      .then((res) => res.data);
  };

  return {
    approveDividend,
    rejectDividend,
    downloadPdf,
    getTodoState,
    hasQualifiedShareDividendReportPermissions,
    getQualifiedShareDividend,
    getCalculatedValues,
    reportFilter,
    getRuleText,
    getStateText,
    getStateLabelClass,
    isStateToApprove,
    getShareholdersDividendInfo,
  };
}

export default useQualifiedShareDividendsService;
